import React from "react";
import { AlertTriangle, ArrowRight, Check, ChevronRight, Download, LayoutDashboard, Mail } from "lucide-react";
import type { ComparisonPage, LandingPage, RelatedLink } from "./types";

export type Navigate = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => void;

/* ---- shared building blocks ---- */

function HeroCtas({ navigate, secondaryHref, secondaryLabel }: { navigate: Navigate; secondaryHref: string; secondaryLabel: string }) {
  return (
    <div className="hero-actions">
      <a className="button button-primary button-lg" href="/download" onClick={navigate("/download")}>
        <Download size={18} /> Download free
      </a>
      <a className="button button-secondary button-lg" href={secondaryHref} onClick={navigate(secondaryHref)}>
        {secondaryLabel} <ArrowRight size={18} />
      </a>
    </div>
  );
}

function ShotFrame({ caption }: { caption: string }) {
  return (
    <figure className="shot-frame">
      <div className="hero-frame">
        <span className="hero-frame-bar" aria-hidden="true">
          <span className="hero-frame-dots">
            <i />
            <i />
            <i />
          </span>
          <span className="hero-frame-title">
            <LayoutDashboard size={13} /> FileAgent
          </span>
        </span>
        <div className="hero-frame-screen">
          <img src="/fileagent-hero-v2.png?v=20260806" alt={caption} loading="lazy" />
        </div>
      </div>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function RelatedLinks({ links, navigate }: { links: RelatedLink[]; navigate: Navigate }) {
  if (!links.length) return null;
  return (
    <section className="section-block shell">
      <div className="section-heading center reveal">
        <p className="eyebrow">Keep reading</p>
        <h2>Related guides &amp; comparisons</h2>
      </div>
      <div className="related-links reveal">
        {links.map((link) => (
          <a key={link.href} className="related-link" href={link.href} onClick={navigate(link.href)}>
            <span>{link.label}</span>
            <ChevronRight size={18} />
          </a>
        ))}
      </div>
    </section>
  );
}

function FinalCta({ navigate, heading, body }: { navigate: Navigate; heading: string; body: string }) {
  return (
    <section className="cta-band reveal">
      <div className="cta-inner">
        <div>
          <h2>{heading}</h2>
          <p>{body}</p>
        </div>
        <div className="cta-actions">
          <a className="button button-primary button-lg" href="/download" onClick={navigate("/download")}>
            <Download size={18} /> Download free
          </a>
          <a className="button button-ghost button-lg" href="/contact-sales" onClick={navigate("/contact-sales")}>
            <Mail size={18} /> Contact sales
          </a>
        </div>
      </div>
    </section>
  );
}

function FaqBlock({ faq }: { faq: { q: string; a: string }[] }) {
  return (
    <section className="section-block shell">
      <div className="section-heading center reveal">
        <p className="eyebrow">Questions</p>
        <h2>Frequently asked questions</h2>
      </div>
      <div className="faq-grid reveal">
        {faq.map((item) => (
          <article className="faq-card" key={item.q}>
            <h3>{item.q}</h3>
            <p>{item.a}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---- Landing page ---- */

export function LandingPageView({ page, navigate }: { page: LandingPage; navigate: Navigate }) {
  return (
    <>
      <section className="hero">
        <div className="shell hero-inner">
          <div className="hero-copy reveal">
            <p className="eyebrow eyebrow-pill">{page.eyebrow}</p>
            <h1>{page.h1}</h1>
            <p className="lead">{page.intro}</p>
            <HeroCtas navigate={navigate} secondaryHref="/pricing" secondaryLabel="Compare plans" />
            <ul className="hero-proof" aria-label="Highlights">
              {page.heroBullets.map((b) => (
                <li key={b}>
                  <Check size={16} /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="hero-media reveal" data-delay="2">
            <ShotFrame caption={page.shotCaption} />
          </div>
        </div>
      </section>

      <section className="section-block shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">The problem</p>
          <h2>{page.problem.heading}</h2>
          <p>{page.problem.body}</p>
        </div>
        <ul className="solution-list lp-painlist reveal">
          {page.problem.points.map((p) => (
            <li key={p}>
              <span className="x-tick" aria-hidden="true">
                <AlertTriangle size={14} />
              </span>
              {p}
            </li>
          ))}
        </ul>
      </section>

      <section className="band">
        <div className="shell">
          <div className="section-heading center reveal">
            <p className="eyebrow">The current way</p>
            <h2>{page.broken.heading}</h2>
            <p>{page.broken.intro}</p>
          </div>
          <div className="problem-grid">
            {page.broken.items.map((item, i) => (
              <article className="problem-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.label}>
                <span className="problem-icon">
                  <AlertTriangle />
                </span>
                <h3>{item.label}</h3>
                <p>{item.pain}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block shell">
        <div className="solution-inner">
          <div className="solution-copy reveal">
            <p className="eyebrow">The FileAgent way</p>
            <h2>{page.solution.heading}</h2>
            <p className="lead">{page.solution.body}</p>
            <ul className="solution-list">
              {page.solution.points.map((p) => (
                <li key={p}>
                  <span className="tick">
                    <Check size={15} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal" data-delay="2">
            <ShotFrame caption={page.shotCaption} />
          </div>
        </div>
      </section>

      <section className="band band-soft">
        <div className="shell">
          <div className="section-heading center reveal">
            <p className="eyebrow">Example workflow</p>
            <h2>{page.workflow.heading}</h2>
            <p>{page.workflow.intro}</p>
          </div>
          <ol className="step-list workflow-steps reveal">
            {page.workflow.steps.map((step, i) => (
              <li key={step.title}>
                <span>{i + 1}</span>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-block shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">Benefits</p>
          <h2>{page.benefits.heading}</h2>
        </div>
        <div className="feature-grid">
          {page.benefits.items.map((item, i) => (
            <article className="feature-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.title}>
              <span className="icon-badge">
                <Check />
              </span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <div className="section-heading center reveal">
            <p className="eyebrow">Features</p>
            <h2>{page.features.heading}</h2>
          </div>
          <div className="feature-grid">
            {page.features.items.map((item, i) => (
              <article className="feature-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FaqBlock faq={page.faq} />
      <RelatedLinks links={page.related} navigate={navigate} />
      <FinalCta
        navigate={navigate}
        heading="Put this workflow on autopilot."
        body="Download free and rebuild this workflow as a managed job in minutes — or talk to us about a larger rollout. No fragile scripts required."
      />
    </>
  );
}

/* ---- Comparison page ---- */

export function ComparisonPageView({ page, navigate }: { page: ComparisonPage; navigate: Navigate }) {
  return (
    <>
      <section className="hero">
        <div className="shell">
          <div className="section-heading center cmp-hero reveal">
            <p className="eyebrow eyebrow-pill">{page.eyebrow}</p>
            <h1>{page.h1}</h1>
            <p className="lead">{page.intro}</p>
            <HeroCtas navigate={navigate} secondaryHref="/pricing" secondaryLabel="Compare plans" />
          </div>
        </div>
      </section>

      <section className="section-block shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">Best fit</p>
          <h2>Which one is right for you?</h2>
        </div>
        <div className="best-fit">
          <article className="best-fit-card featured reveal">
            <h3>Choose FileAgent if…</h3>
            <p>{page.bestFit.fileagent}</p>
          </article>
          <article className="best-fit-card reveal" data-delay="1">
            <h3>{page.altName} may fit if…</h3>
            <p>{page.bestFit.alternative}</p>
          </article>
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <div className="section-heading center reveal">
            <p className="eyebrow">Side by side</p>
            <h2>{page.tableTitle}</h2>
          </div>
          <div className="vs-table-wrap reveal">
            <table className="vs-table">
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col" className="is-featured">
                    FileAgent
                  </th>
                  <th scope="col">{page.altName}</th>
                </tr>
              </thead>
              <tbody>
                {page.table.map((row) => (
                  <tr key={row.feature}>
                    <th scope="row">{row.feature}</th>
                    <td className="is-featured">
                      <Check size={15} aria-hidden="true" /> {row.fileagent}
                    </td>
                    <td>{row.alt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section-block shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">Why FileAgent</p>
          <h2>{page.whyHeading}</h2>
        </div>
        <div className="feature-grid">
          {page.why.map((item, i) => (
            <article className="feature-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.title}>
              <span className="icon-badge">
                <Check />
              </span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="band band-soft">
        <div className="shell">
          <div className="when-panel reveal">
            <h2>{page.whenHeading}</h2>
            <p>{page.whenBody}</p>
            <ul className="solution-list">
              {page.whenPoints.map((p) => (
                <li key={p}>
                  <span className="tick">
                    <Check size={15} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <FaqBlock faq={page.faq} />
      <RelatedLinks links={page.related} navigate={navigate} />
      <FinalCta
        navigate={navigate}
        heading="See FileAgent for yourself."
        body="Download the free plan and run a real transfer today, or compare plans for your team."
      />
    </>
  );
}
